// src/padplanner/ChecksLayers.js
//
// The editor's two check overlays on the map (S5b, mockup 4):
//
//   Layout checks    a witness line for each failing distance result (the
//                    server's shortest line), labeled "84′ · min 100′" in a
//                    DOM overlay; and the live reading while dragging
//                    (clearanceMirror.js), dashed, until a save's results
//                    replace it.
//   Safety redzones  the halo around each well-linked feature: the union of
//                    the published min_distance limits it is subject to.
//
// Each is one layer toggle; hidden, its lines, fills and labels all go.
//
// ── IDS ─────────────────────────────────────────────────────────────────────
//
// ck-*: not pp-* (PadPlannerMap's) and not fe-* (the FootprintEditor's). The
// redzones sit under the editor's features (before its fill layer), the
// witness lines over them. destroy() removes what this added.
//
// ── LABELS STAY CHEAP AT 1,460 RESULTS ──────────────────────────────────────
//
// As the editor's feature labels: spans pooled and reused, points off screen
// skipped, one per LABEL_CELL screen cell, at most MAX_WITNESS_LABELS. The
// live label is drawn first and is never culled.
import { witnessText, DEFER_TEXT } from "./checksVocabulary";
import { DISTANCE_RELATIONS } from "./ruleVocabulary";

export const MAX_WITNESS_LABELS = 200;
const LABEL_CELL = { w: 110, h: 20 };
const LABEL_MARGIN = 48;
const EMPTY = { type: "FeatureCollection", features: [] };
export const CHECK_IDS = Object.freeze({
  witness: "ck-witness", live: "ck-live", redzones: "ck-redzones",
  witnessLine: "ck-witness-line", liveLine: "ck-live-line", redzoneFill: "ck-redzone-fill", redzoneLine: "ck-redzone-line",
});
// Map-data colors, as mockup 4 draws them: a red witness, dashed orange halos.
// A light Paper sheet passes its own (setDrawingStyle; padplanner/paperTheme.js).
const RED = "#ef4444";
const ORANGE = "#f97316";
const DEFERRED = "#f8fafc";
const livePaint = (witness, deferred) => ["case", ["get", "deferred"], deferred, witness];

const midpoint = (coords) => {
  if (!coords?.length) return null;
  if (coords.length === 1) return coords[0];
  const a = coords[0], b = coords[coords.length - 1];
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
};

export default class ChecksLayers {
  /** @param {{map: object, below?: string|null}} o  below: the layer id the redzones go under (the editor's fill) */
  constructor({ map, below = null }) {
    this.map = map;
    this.visible = { layout: true, redzones: true };
    this.witness = [];
    this.live = null;
    const m = map;
    for (const id of [CHECK_IDS.witness, CHECK_IDS.live, CHECK_IDS.redzones]) m.addSource(id, { type: "geojson", data: EMPTY });
    const under = below && m.getLayer(below) ? below : undefined;
    m.addLayer({ id: CHECK_IDS.redzoneFill, type: "fill", source: CHECK_IDS.redzones, filter: ["==", ["geometry-type"], "Polygon"],
      paint: { "fill-color": ORANGE, "fill-opacity": 0.08 } }, under);
    m.addLayer({ id: CHECK_IDS.redzoneLine, type: "line", source: CHECK_IDS.redzones, filter: ["==", ["geometry-type"], "LineString"],
      paint: { "line-color": ORANGE, "line-width": 1.75, "line-dasharray": [3, 2] } }, under);
    m.addLayer({ id: CHECK_IDS.witnessLine, type: "line", source: CHECK_IDS.witness, paint: { "line-color": RED, "line-width": 2 } });
    m.addLayer({ id: CHECK_IDS.liveLine, type: "line", source: CHECK_IDS.live,
      paint: { "line-color": livePaint(RED, DEFERRED), "line-width": 2, "line-dasharray": [2, 1.5] } });

    const container = m.getContainer?.();
    if (container) {
      const el = document.createElement("div");
      el.className = "ck-overlay";
      el.dataset.testid = "pp-checks-overlay";
      Object.assign(el.style, { position: "absolute", inset: "0", pointerEvents: "none", overflow: "hidden" });
      container.appendChild(el);
      this._overlay = el;
      this._pool = [];
    }
    this._onMove = () => this._renderLabels();
    m.on("move", this._onMove);
  }

  get ids() {
    return { sources: [CHECK_IDS.witness, CHECK_IDS.live, CHECK_IDS.redzones],
      layers: [CHECK_IDS.redzoneFill, CHECK_IDS.redzoneLine, CHECK_IDS.witnessLine, CHECK_IDS.liveLine] };
  }

  destroy() {
    const m = this.map;
    if (this._destroyed) return;
    this._destroyed = true;
    this._overlay?.remove();
    if (m._removed) return;
    m.off("move", this._onMove);
    for (const l of [...this.ids.layers].reverse()) if (m.getLayer(l)) m.removeLayer(l);
    for (const s of this.ids.sources) if (m.getSource(s)) m.removeSource(s);
  }

  /** The failing distance results with a witness: the server's shortest lines. */
  setResults(results) {
    if (this._destroyed) return;
    this.witness = (results ?? [])
      .filter((r) => r.outcome === "fail" && DISTANCE_RELATIONS.includes(r.relation) && r.witness?.coordinates?.length)
      .map((r) => ({ geometry: r.witness, text: witnessText(r), severity: r.severity, at: midpoint(r.witness.coordinates) }));
    this.map.getSource(CHECK_IDS.witness)?.setData({
      type: "FeatureCollection",
      features: this.witness.map((w) => ({ type: "Feature", properties: { severity: w.severity }, geometry: w.geometry })),
    });
    this._renderLabels();
  }

  /** The live reading (clearanceMirror.liveReading), or null. */
  setLive(reading) {
    if (this._destroyed) return;
    this.live = reading ?? null;
    this.map.getSource(CHECK_IDS.live)?.setData(reading ? {
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: { deferred: reading.deferred }, geometry: { type: "LineString", coordinates: [reading.from, reading.to] } }],
    } : EMPTY);
    this._renderLabels();
  }

  /** clearanceMirror.redzones()'s fill and outline. */
  setRedzones(z) {
    if (this._destroyed) return;
    this.map.getSource(CHECK_IDS.redzones)?.setData(z ? { type: "FeatureCollection", features: [...z.fill.features, ...z.outline.features] } : EMPTY);
  }

  setVisible({ layout = this.visible.layout, redzones = this.visible.redzones } = {}) {
    if (this._destroyed) return;
    this.visible = { layout, redzones };
    const v = (on) => (on ? "visible" : "none");
    this.map.setLayoutProperty(CHECK_IDS.witnessLine, "visibility", v(layout));
    this.map.setLayoutProperty(CHECK_IDS.liveLine, "visibility", v(layout));
    this.map.setLayoutProperty(CHECK_IDS.redzoneFill, "visibility", v(redzones));
    this.map.setLayoutProperty(CHECK_IDS.redzoneLine, "visibility", v(redzones));
    this._renderLabels();
  }

  /**
   * The lines' colors for a drawing style (mapView.js drawingStyleFor):
   * redzone, witness and deferred; any absent is the map's own. The labels
   * carry their own dark chips and do not change.
   */
  setDrawingStyle({ redzone = null, witness = null, deferred = null } = {}) {
    if (this._destroyed) return;
    const halo = redzone ?? ORANGE;
    this.map.setPaintProperty(CHECK_IDS.redzoneFill, "fill-color", halo);
    this.map.setPaintProperty(CHECK_IDS.redzoneLine, "line-color", halo);
    this.map.setPaintProperty(CHECK_IDS.witnessLine, "line-color", witness ?? RED);
    this.map.setPaintProperty(CHECK_IDS.liveLine, "line-color", livePaint(witness ?? RED, deferred ?? DEFERRED));
  }

  /** The labels drawn now: [{text, x, y, live}], after the cull and the cap. */
  visibleLabels() {
    if (!this.visible.layout) return [];
    const c = this.map.getContainer?.();
    const w = c?.clientWidth ?? 0, h = c?.clientHeight ?? 0;
    const out = [];
    if (this.live) {
      const p = this.map.project(midpoint([this.live.from, this.live.to]));
      const text = this.live.deferred ? DEFER_TEXT
        : witnessText({ relation: this.live.rule.relation, measured_ft: this.live.ft, limit_ft: this.live.rule.limit_ft });
      out.push({ text, x: p.x, y: p.y, live: true, deferred: this.live.deferred });
    }
    const taken = new Set();
    for (const l of this.witness) {
      if (out.length >= MAX_WITNESS_LABELS + (this.live ? 1 : 0)) break;
      if (!l.at || !l.text) continue;
      const p = this.map.project(l.at);
      if (p.x < -LABEL_MARGIN || p.y < -LABEL_MARGIN || p.x > w + LABEL_MARGIN || p.y > h + LABEL_MARGIN) continue;
      const cell = `${Math.floor(p.x / LABEL_CELL.w)}:${Math.floor(p.y / LABEL_CELL.h)}`;
      if (taken.has(cell)) continue;
      taken.add(cell);
      out.push({ text: l.text, x: p.x, y: p.y, live: false });
    }
    return out;
  }

  _renderLabels() {
    const box = this._overlay;
    if (!box || this._destroyed) return;
    const shown = this.visibleLabels();
    const pool = this._pool;
    while (pool.length < shown.length) {
      const s = document.createElement("span");
      s.className = "ck-label";
      Object.assign(s.style, {
        position: "absolute", left: "0", top: "0", font: "600 11px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace",
        padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap", border: "1px solid rgba(239, 68, 68, 0.7)",
      });
      box.appendChild(s);
      pool.push(s);
    }
    pool.forEach((s, i) => {
      const l = shown[i];
      if (!l) { if (s.style.display !== "none") s.style.display = "none"; return; }
      if (s.textContent !== l.text) s.textContent = l.text;
      s.dataset.live = l.live ? "true" : "false";
      s.style.display = "";
      s.style.color = l.deferred ? "#f8fafc" : "#fca5a5";
      s.style.background = l.deferred ? "rgba(15, 23, 42, 0.9)" : "rgba(69, 10, 10, 0.9)";
      s.style.transform = `translate(${l.x}px, ${l.y}px) translate(-50%, -50%)`;
    });
  }
}
